<template>
    <NuxtLayout name="casino">
        <div class="ringGame relative max-w-[650px]">
            <ClientOnly>
                <GameSettingsDiceTabsWrapper class="flex !justify-end !mb-3">
                    <GameSettingsButtonLiveChat v-if="useUserStore().isLoggedIn" v-model="isOpenLiveChat" />
                    <GameSettingsButtonAnimate v-model="settingStore.animation" />
                    <GameSettingsButtonFast v-model="game.ring.fast" />
                    <GameSettingsButtonFairness v-model="isOpenFairness" />
                    <GameSettingsButtonRule v-model="isOpenRule" name="ring" />
                    <GameSettingsButtonVocie
                        v-model="settingStore.inhouseGames.sound.on"
                        v-model:voice-model-value="settingStore.inhouseGames.sound.music"
                        :saveAfterChange="true"
                    >
                    </GameSettingsButtonVocie>
                </GameSettingsDiceTabsWrapper>
            </ClientOnly>
            <div class="rounded-md">
                <div class="relative overflow-hidden">
                    <ClientOnly>
                        <Rings
                            :oForm="oForm"
                            :risk="risksOptions.value"
                            :segmentsModel="SegmentsOptions.value"
                            :rotateNum="rotateNum"
                            :once-bet="onceBet"
                            :animation="settingStore.animation"
                            :isFast="game.ring.fast"
                            :is-win="isWin"
                            :win-amount="winAmount"
                            :delay="delay"
                            :resultShow="resultShow"
                        />
                    </ClientOnly>
                    <Pickers
                        :rotate-num="rotateNum"
                        :risk="risksOptions.value"
                        :segments="SegmentsOptions.value"
                        :onceBet="onceBet"
                        :amount="oForm.amount"
                        @update:modelResult="isResultFn"
                    />
                </div>
            </div>
            <div class="grid grid-cols-2 gap-2 mt-8 font-semibold">
                <div class="text-grey-40 flex flex-col gap-3 text-[15px]">
                    <ClientOnly>
                        <DropDown 
                            v-if="risksOptions.options.length > 0" 
                            v-model="risksOptions.value"
                            :options="risksOptions.options" 
                            class="w-full"
                            :disabled="oForm.isSubmiting"
                            :label="t.risk"
                        >
                            <template #prevIcon>
                                <span class="text-sm font-semibold text-[#55657e]">
                                </span>
                            </template>
                        </DropDown>
                    </ClientOnly>
                </div>
                <div class="text-grey-40 flex flex-col gap-3 text-[15px]">
                    <ClientOnly>
                        <DropDown 
                            v-if="SegmentsOptions.options.length > 0" 
                            v-model="SegmentsOptions.value"
                            :options="SegmentsOptions.options" 
                            class="w-full"
                            :disabled="oForm.isSubmiting"
                            :label="t.GameRing.Segments"
                        >
                            <template #prevIcon>
                                <span class="text-sm font-semibold text-[#55657e]">
                                </span>
                            </template>
                        </DropDown>
                    </ClientOnly>
                </div>
            </div>
            <ClientOnly>
                <CasinoBetOptionSection
                    :betTookit="betTookit"
                    :auto-bet-tool-kit="autoBetToolKit"
                    v-model:bet-amount-modal-value="oForm.amount"
                    v-model:is-submiting="oForm.isSubmiting"
                    :sound="settingStore.globalSounds"
                    :isAuto="game.ring.auto.default"
                    :bet-submit="betSubmit"
                    :is-fast="game.ring.fast"
                    @change-mode="toChangeMode"
                />
                <CasinoAutoSection
                    v-model:amount="oForm.amount"
                    :ref="el => oAutoDialog=el"
                    :is-fast="game.ring.fast"
                    :is-submiting="oForm.isSubmiting"
                    :is-auto="game.ring.auto.default"
                />
            </ClientOnly>
        </div>
    </NuxtLayout>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, watch, defineComponent } from 'vue';
import useTranslation from '~/i18n/useTransLation';
// socket
import { useGameSocket } from '~/socket/game';
// stores
import useSettingsStore from '~/stores/settings';
import { useUserStore } from '~/stores/user';
import { useJoinStateStore } from '@/stores/cansino/joinState';
// functions
import { useBettingToolkit } from '~/common/js/bettingToolkit';
import { useAutoBetToolKit } from '~/common/js/bettingToolkit/auto';
// components
import Rings from './_components/Rings/index.vue';
import Pickers from './_components/Pickers.vue';
// assets
import ringBGM from '~/assets/mp3/ring/ring_bg.mp3';
import winSound from '~/assets/mp3/ring/ring_win.mp3';
import loseSound from '~/assets/mp3/ring/ring_loss.mp3';
import ringBet from '~/assets/mp3/ring/ring_bet.mp3';
import ringScrol from '~/assets/mp3/ring/ring_scrol.mp3';

const {t: $tFn} = useNuxtApp().$i18n;
const t = useTranslation();
const isOpenFairness = ref(false);
const isOpenRule = ref(false);
const isOpenLiveChat = ref(false);
const onceBet = ref(false);
const rotateNum = ref(0);
const isWin = ref(false);
const winAmount = ref(0);
// 下注后结果展示
const resultShow = ref(true)
const SegmentsOptions = reactive({
    options: [
        {
            name: 10,
            value: 10,
        },
        {
            name: 20,
            value: 20,
        },
        {
            name: 30,
            value: 30,
        },
        {
            name: 40,
            value: 40,
        },
        {
            name: 50,
            value: 50,
        },
    ],
    value: 50,
    odds: [0.0244, 0.8544, 1.7089, 10.253, 15.38, 51.269, 20.507],
});
const risksOptions = reactive({
    options: [
        {
            name: $tFn('commonInfo.riskLow'),
            value: 'low',
        },
        {
            name: $tFn('commonInfo.riskMedium'),
            value: 'medium',
        },
        {
            name: $tFn('commonInfo.riskMediumHigh'),
            value: 'medium-high',
        },
        {
            name: $tFn('commonInfo.riskHigh'),
            value: 'high',
        },
    ],
    value: 'medium',
});

const settingStore = useSettingsStore();
const joinStateStore = useJoinStateStore()
const { game } = storeToRefs(settingStore);
const { EMIT_RING_BET } = useGameSocket();

const oMusicLib = {
    ringBGM: {
        src: ringBGM,
        loop: true,
        isBGM: true,
    },
    win: {
        src: winSound,
    },
    lose: {
        src: loseSound,
    },
    bet: {
        src: ringBet,
    },
    scrol: {
        src: ringScrol,
    },
};
let musicControler = null;

const oForm = reactive({
    amount: 0,
    isSubmiting: false,
});
const betTookit = useBettingToolkit(oForm);

// ↓ 自动模式输入面板“窗口化改造” 添加的变量与方法： 
const oAutoDialog = ref(null);
const delay = ref(400);
const toChangeMode = (isAuto)=>{
    settingStore.game.ring.auto.default = !!isAuto;
    if (isAuto){
        oAutoDialog.value.switchAutoDialogVisible(true);
    }
};


const betSubmit = async () => {
    if (onceBet.value){
        onceBet.value = false;
    }
    
    musicControler?.oAudio.bet.toPlay();
    musicControler?.oAudio.scrol.toPlay();
    const ts = useUserStore().queueStart(); // 防止余额立即变化
    let params = {
        amount: oForm.amount.beFixed(2),
        frontID: new Date().getTime(),
        isFast: game.value.ring.fast,
        risk: risksOptions.value.toLowerCase(),
        segments: SegmentsOptions.value,
    };
    const isAuto = Boolean(settingStore.game.ring.auto.default); 
    // if (import.meta.env.DEV_MODE && isAuto && Math.random() <= 0.2){ 
    //     params.amount = '0'; 
    // }
    const res = await EMIT_RING_BET(params);
    if (!res || res.error){
        useUserStore().queueDelete(ts);
        isAuto || showNegativeNotify(res?.error);
        return res;
    }
    rotateNum.value = Number(res.roll_number);
    winAmount.value = Number(res.winAmount);

    if (res.winAmount > 0) {
        isWin.value = true;
        musicControler?.oAudio.win.toPlay();
    } else {
        isWin.value = false;
        musicControler?.oAudio.lose.toPlay();
    }
    // 延时展示，等待动画执行完毕
    await Sleep(delay.value);
    joinStateStore.showDateInQueue(); // 插入游戏结果并播放动画
    useUserStore().queueForward(ts); // 显示余额变化
    onceBet.value = true;
    // 下注后，展示结果
    resultShow.value = false
    return res;
};
const isResultFn= () =>{
    // 难度和段变化， 结果展示默认底图
    resultShow.value =true
}

const autoBetToolKit = useAutoBetToolKit(oForm, betSubmit);

onMounted(() => {
    onUnmounted(() => {
        musicControler?.toDestory();
    });
});

watch(
    () => settingStore.inhouseGames.sound.on,
    (val) => {
        if (import.meta.server) return;
        if (val) {
            if (!musicControler) {
                musicControler = new MusicControler(oMusicLib as any);
            }
            musicControler.oAudio.ringBGM.toPlay();
        } else {
            musicControler?.oAudio.ringBGM.stop();
        }
    },
    { immediate: true },
);

watch(
    () => game.value.ring.fast,
    (val) => {
        if (!val) {
            delay.value = 800;
        } else {
            delay.value = 400;
        }
    },
    {
        immediate: true
    }
);
</script>

<script lang="ts">
defineComponent({
    name: 'RingGame',
});
</script>
<style lang="scss" scoped>
.ringGame {
    line-height: normal;
}
</style>
